import { Fragment, useState } from 'react'
import { ActiveSession } from '@weblite/ts-action/auth'
import { Divider, Skeleton, Stack, styled, SxProps } from '@mui/material'
import { SettingsSection } from '$common/settings-section'
import { ListItemTextButton } from '$common/buttons/list-item-text-button'
import { SESSIONS_PAGE_SIZE } from '$logic/auth/constants'
import { SessionItem } from './session-item'
import { t } from '$i18n'

const InfixDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

const SessionItemSkeleton = () => {
  return (
    <div
      style={{
        height: '100px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxSizing: 'border-box',
        padding: '13px 20px 14px',
      }}
    >
      <Stack alignItems="flex-start" gap="4px">
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="text" width={80} height={20} />
      </Stack>
      <Skeleton variant="rectangular" width={120} height={36} />
    </div>
  )
}

interface SessionsListProps {
  activeSessions: ActiveSession[]
  terminateSession: (selector: string) => Promise<void>
  isLoading: boolean
  sx?: SxProps
}

export const SessionsList = ({
  activeSessions,
  terminateSession,
  isLoading,
  sx,
}: SessionsListProps) => {
  if (isLoading)
    return (
      <Stack spacing={2} sx={{ width: '100%', overflow: 'auto', ...sx }}>
        <SettingsSection
          headerText={t('userActiveSessionsLimitModal.allowedDevices')}
        >
          <div style={{ width: '100%' }}>
            {[1, 2, 3].map(item => (
              <Fragment key={item}>
                <SessionItemSkeleton />
                <InfixDivider />
              </Fragment>
            ))}
          </div>
        </SettingsSection>
      </Stack>
    )

  if (!activeSessions.length) return null

  return (
    <Stack spacing={2} sx={{ width: '100%', overflow: 'auto', ...sx }}>
      <SettingsSection
        headerText={t('userActiveSessionsLimitModal.allowedDevices')}
      >
        <VisibleSessions
          activeSessions={activeSessions}
          terminateSession={terminateSession}
        />
      </SettingsSection>
    </Stack>
  )
}

/**
 * Renders the list in pages. A user who signs in often can accumulate hundreds
 * of sessions, and rendering them all at once locks up low-end devices — which
 * is what this modal runs on.
 */
const VisibleSessions = ({
  activeSessions,
  terminateSession,
}: Pick<SessionsListProps, 'activeSessions' | 'terminateSession'>) => {
  const [visibleCount, setVisibleCount] = useState(SESSIONS_PAGE_SIZE)

  const visibleSessions = activeSessions.slice(0, visibleCount)
  const remainingCount = activeSessions.length - visibleSessions.length

  return (
    <div style={{ width: '100%' }}>
      {visibleSessions.map(({ identity, selector }) => (
        <Fragment key={selector}>
          <SessionItem
            userAgent={identity?.userAgent}
            onClick={() => terminateSession(selector)}
          />
          <InfixDivider />
        </Fragment>
      ))}

      {remainingCount > 0 && (
        <>
          <Divider />
          <ListItemTextButton
            value={t('userActiveSessionsLimitModal.showMore')(remainingCount)}
            onClick={() =>
              setVisibleCount(current => current + SESSIONS_PAGE_SIZE)
            }
          />
        </>
      )}
    </div>
  )
}
